import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openRazorpay } from "@/lib/razorpay";
import { Check, ShieldCheck, Lock, Sparkles } from "lucide-react";

export default function Payment() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const me = await refreshMe();
      if (me.status === "active") {
        if (!me.profile_complete) nav("/profile/setup");
        else nav("/dashboard");
        return;
      }
      const { data } = await api.get("/plans");
      setPlans(data);
      if (data.length) setSelected(data[Math.min(1, data.length - 1)].package_id);
    })();
    // eslint-disable-next-line
  }, []);

  const pay = async () => {
    if (!selected) return;
    setBusy(true);
    await openRazorpay({
      package_id: selected,
      intent: "subscription",
      onSuccess: async () => {
        const me = await refreshMe();
        if (!me.profile_complete) nav("/profile/setup");
        else nav("/dashboard");
      },
      onDismiss: () => setBusy(false),
    });
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 h-24 flex items-center gap-2">
          <img src="/brand/logo.png" alt="Traveloble" className="h-20 w-auto select-none" draggable="false" />
          <div className="ml-auto text-sm text-slate-500">Step 2 of 3 — Choose your plan</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="overline mb-2 text-emerald-700">Activate your account</div>
        <h1 className="font-editorial text-4xl text-slate-900">Choose your plan &amp; pay securely</h1>
        <p className="text-sm text-slate-500 mt-3 max-w-2xl">
          Signed in as <b>{user?.email}</b>. Pick a plan below and complete payment via Razorpay
          — your account activates the moment the payment is captured, no waiting.
        </p>

        {/* Plans */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`plan-card-${p.package_id}`}
              onClick={() => setSelected(p.package_id)}
              className={`text-left rounded p-7 transition-all ${
                selected === p.package_id
                  ? "bg-[#0F1E5B] text-white ring-4 ring-emerald-500/40"
                  : "bg-white border border-border hover:border-emerald-500"
              }`}
            >
              <div className={`overline ${selected === p.package_id ? "!text-[#BBF7D0]" : ""}`}>{p.name}</div>
              <div className="font-editorial text-4xl mt-3">
                ₹{p.price.toLocaleString()}
                <span className={`text-sm font-body ml-1 ${selected === p.package_id ? "text-white/60" : "text-slate-500"}`}>/year</span>
              </div>
              <ul className={`mt-6 space-y-2.5 text-sm ${selected === p.package_id ? "text-white/90" : "text-slate-700"}`}>
                {p.features?.map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <Check size={14} className={selected === p.package_id ? "text-[#BBF7D0] mt-0.5" : "text-[#16A34A] mt-0.5"} />
                    {x}
                  </li>
                ))}
              </ul>
              <div className={`mt-6 text-[11px] uppercase tracking-widest ${selected === p.package_id ? "text-[#BBF7D0]" : "text-emerald-700"}`}>
                {selected === p.package_id ? "✓ Selected" : "Click to select"}
              </div>
            </button>
          ))}
        </div>

        {/* Checkout CTA */}
        <div className="mt-10 bg-white border border-border rounded-lg p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded bg-[#DCFCE7] flex items-center justify-center shrink-0">
              <ShieldCheck size={20} className="text-[#16A34A]" />
            </div>
            <div>
              <div className="font-editorial text-xl text-slate-900">Secure checkout by Razorpay</div>
              <div className="text-sm text-slate-500 mt-1">
                Cards, UPI, netbanking, wallets &amp; EMI accepted. Payment settled directly to Traveloble.
              </div>
            </div>
          </div>
          <button
            data-testid="pay-razorpay-btn"
            onClick={pay}
            disabled={busy || !selected}
            className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded text-sm font-semibold shrink-0"
          >
            <Lock size={14} /> {busy ? "Opening checkout…" : "Pay & Activate"}
          </button>
        </div>

        <p className="mt-6 text-xs text-slate-500 text-center inline-flex items-center gap-1.5 w-full justify-center">
          <Sparkles size={12} className="text-emerald-600" />
          Instant activation — subscription begins the moment your payment is captured.
        </p>
      </div>
    </div>
  );
}
